import React, { memo, useRef, useEffect } from 'react'
import * as THREE from 'three'
import { useThree } from '@react-three/fiber'
import type {
  LightSpec,
  SpotLightSpec,
  DirectionalLightSpec,
} from '../types'
import { detectGPUTier, type GPUTier } from '../../three/gpuTier'

type ShadowQuality = GPUTier

export interface ProductLightsProps {
  enableShadows?: boolean
  shadowQuality?: ShadowQuality
  rig: LightSpec[]
}

const SpotLightWithTarget: React.FC<{
  light: SpotLightSpec
  shadowMapSize: number
  enableShadows: boolean
}> = ({ light, shadowMapSize, enableShadows }) => {
  const lightRef = useRef<THREE.SpotLight>(null)
  const { scene } = useThree()

  useEffect(() => {
    const spot = lightRef.current
    if (!spot) return
    spot.target.position.set(...light.target)
    scene.add(spot.target)
    spot.target.updateMatrixWorld()
    return () => {
      scene.remove(spot.target)
    }
  }, [scene, light.target])

  const castShadow = (light.castShadow ?? false) && enableShadows
  return (
    <spotLight
      ref={lightRef}
      position={light.position}
      intensity={light.intensity}
      angle={light.angle}
      penumbra={light.penumbra}
      distance={light.distance ?? 0}
      decay={light.decay ?? 0}
      color={light.color}
      castShadow={castShadow}
      shadow-mapSize-width={shadowMapSize}
      shadow-mapSize-height={shadowMapSize}
      shadow-camera-near={10}
      shadow-camera-far={6000}
      shadow-bias={-0.0001}
      shadow-normalBias={0.02}
      shadow-radius={light.shadowRadius ?? 2}
      shadow-blurSamples={light.shadowBlurSamples ?? 8}
    />
  )
}

const DirectionalLightWithTarget: React.FC<{
  light: DirectionalLightSpec
  shadowMapSize: number
  enableShadows: boolean
}> = ({ light, shadowMapSize, enableShadows }) => {
  const lightRef = useRef<THREE.DirectionalLight>(null)
  const { scene } = useThree()

  useEffect(() => {
    const dir = lightRef.current
    if (!dir) return
    dir.target.position.set(...light.target)
    scene.add(dir.target)
    dir.target.updateMatrixWorld()
    return () => {
      scene.remove(dir.target)
    }
  }, [scene, light.target])

  const castShadow = (light.castShadow ?? false) && enableShadows
  return (
    <directionalLight
      ref={lightRef}
      position={light.position}
      intensity={light.intensity}
      color={light.color}
      castShadow={castShadow}
      shadow-mapSize-width={shadowMapSize}
      shadow-mapSize-height={shadowMapSize}
      shadow-camera-near={50}
      shadow-camera-far={2000}
      shadow-camera-left={-800}
      shadow-camera-right={800}
      shadow-camera-top={800}
      shadow-camera-bottom={-800}
      shadow-bias={-0.0001}
      shadow-normalBias={0.02}
    />
  )
}

function renderLight(
  light: LightSpec,
  index: number,
  shadowMapSize: number,
  enableShadows: boolean
) {
  switch (light.type) {
    case 'ambient':
      return (
        <ambientLight
          key={index}
          intensity={light.intensity}
          color={light.color}
        />
      )
    case 'spot':
      return (
        <SpotLightWithTarget
          key={index}
          light={light}
          shadowMapSize={shadowMapSize}
          enableShadows={enableShadows}
        />
      )
    case 'directional':
      return (
        <DirectionalLightWithTarget
          key={index}
          light={light}
          shadowMapSize={shadowMapSize}
          enableShadows={enableShadows}
        />
      )
    case 'point': {
      const castShadow = (light.castShadow ?? false) && enableShadows
      return (
        <pointLight
          key={index}
          position={light.position}
          intensity={light.intensity}
          distance={light.distance ?? 0}
          decay={light.decay ?? 0}
          color={light.color}
          castShadow={castShadow}
          shadow-mapSize-width={shadowMapSize}
          shadow-mapSize-height={shadowMapSize}
        />
      )
    }
  }
}

const ProductLightsComponent: React.FC<ProductLightsProps> = ({
  enableShadows = true,
  shadowQuality,
  rig,
}) => {
  const detected = detectGPUTier()
  const effectiveQuality: ShadowQuality = shadowQuality
    ? shadowQuality === 'high' && detected !== 'high'
      ? detected
      : shadowQuality
    : detected
  const shadowMapSize =
    effectiveQuality === 'high'
      ? 4096
      : effectiveQuality === 'medium'
        ? 2048
        : 1024

  return (
    <>
      {rig.map((light, i) =>
        renderLight(light, i, shadowMapSize, enableShadows)
      )}
    </>
  )
}

ProductLightsComponent.displayName = 'ProductLights'

export const ProductLights = memo(ProductLightsComponent)
